import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  PlugsConnected, Plus, Trash, PaperPlaneTilt, Info, ClockCounterClockwise,
  CheckCircle, XCircle, Copy, ArrowsClockwise, Pencil, Lightning,
} from "@phosphor-icons/react";

const EVENT_GROUPS = [
  { label: "Chantiers", events: ["chantier.created", "chantier.updated", "chantier.completed", "chantier.deleted"] },
  { label: "Interventions", events: ["intervention.created", "intervention.done", "intervention.validated", "intervention.arrival_notified"] },
  { label: "SAV / Alertes", events: ["sav.created", "sav.assigned", "sav.converted", "sav.resolved", "sav.rated"] },
  { label: "Devis & Rapports", events: ["quote.flagged", "quote.accepted", "report.emailed", "report.opened_by_client"] },
  { label: "Équipe", events: ["chat.posted", "user.invited", "leave.approved"] },
];

/** Admin config for outbound webhooks (n8n / Zapier / Make). */
export default function OutboundWebhooksPanel() {
  const [hooks, setHooks] = useState([]);
  const [available, setAvailable] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deliveries, setDeliveries] = useState({ webhookId: null, list: [] });

  const load = async () => {
    try {
      const r = await api.get("/admin/webhooks");
      setHooks(r.data.webhooks || []);
      setAvailable(r.data.available_events || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const create = () => setEditing({ name: "", url: "", events: ["chantier.completed", "sav.resolved"], enabled: true });
  const toggle = async (h) => {
    try {
      await api.patch(`/admin/webhooks/${h.id}`, { enabled: !h.enabled });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (h) => {
    if (!window.confirm(`Supprimer le webhook « ${h.name} » ?`)) return;
    try {
      await api.delete(`/admin/webhooks/${h.id}`);
      toast.success("Supprimé");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const test = async (h) => {
    try {
      const r = await api.post(`/admin/webhooks/${h.id}/test`);
      if (r.data.ok) toast.success(`Ping OK · ${r.data.status} · ${r.data.duration_ms}ms`);
      else toast.error(`Échec · ${r.data.status || r.data.error || "erreur réseau"}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const showDeliveries = async (h) => {
    try {
      const r = await api.get(`/admin/webhooks/${h.id}/deliveries`);
      setDeliveries({ webhookId: h.id, name: h.name, list: r.data || [] });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-4" data-testid="webhooks-panel">
      <div className="rounded-2xl bg-[#005CFF]/8 border border-[#005CFF]/25 p-4 flex gap-3 items-start">
        <PlugsConnected size={18} weight="duotone" className="text-[#7CB2FF] shrink-0 mt-0.5" />
        <div className="text-sm text-white/80 leading-relaxed">
          Envoie automatiquement les événements BKflow (nouveau chantier, ticket résolu, avis client…) vers <b>n8n</b>, <b>Zapier</b>, <b>Make.com</b> ou tout endpoint HTTP.
          <div className="text-xs text-white/50 mt-1">
            Chaque appel est signé <code>X-BKflow-Signature: sha256=…</code> avec le secret du webhook pour authentification côté récepteur.
          </div>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {hooks.length === 0 && (
          <div className="rounded-xl bg-white/[0.02] border border-dashed border-white/10 p-6 text-center text-sm text-white/40">
            Aucun webhook configuré. Ajoute ton premier endpoint pour commencer.
          </div>
        )}
        {hooks.map((h) => (
          <div key={h.id} className="rounded-2xl bg-white/[0.03] border border-white/10 p-3" data-testid={`webhook-row-${h.id}`}>
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${h.enabled ? "bg-emerald-500/15 border border-emerald-500/30" : "bg-white/5 border border-white/10"}`}>
                <Lightning size={16} weight="fill" className={h.enabled ? "text-emerald-400" : "text-white/40"} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate flex items-center gap-2">
                  {h.name}
                  {h.last_status && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${h.last_status && (h.last_status < 300) ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`} title={`Dernier appel : ${h.last_call_at || "—"}`}>
                      {h.last_status}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-white/40 truncate font-mono">{h.url}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(h.events || []).slice(0, 4).map((e) => (
                    <span key={e} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#005CFF]/15 text-[#7CB2FF]">{e}</span>
                  ))}
                  {(h.events || []).length > 4 && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-white/40">+{h.events.length - 4}</span>
                  )}
                </div>
                <div className="text-[9.5px] text-white/40 mt-1">
                  ✓ {h.success_count || 0} · ✗ {h.failure_count || 0}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Switch checked={h.enabled} onCheckedChange={() => toggle(h)} data-testid={`webhook-toggle-${h.id}`} />
                <button onClick={() => test(h)} className="text-white/40 hover:text-[#7CB2FF] p-1.5" title="Tester (ping)" data-testid={`webhook-test-${h.id}`}>
                  <PaperPlaneTilt size={15} weight="duotone" />
                </button>
                <button onClick={() => showDeliveries(h)} className="text-white/40 hover:text-white/80 p-1.5" title="Historique" data-testid={`webhook-history-${h.id}`}>
                  <ClockCounterClockwise size={15} weight="duotone" />
                </button>
                <button onClick={() => setEditing(h)} className="text-white/40 hover:text-[#005CFF] p-1.5" title="Modifier" data-testid={`webhook-edit-${h.id}`}>
                  <Pencil size={14} weight="duotone" />
                </button>
                <button onClick={() => remove(h)} className="text-white/40 hover:text-red-400 p-1.5" title="Supprimer" data-testid={`webhook-delete-${h.id}`}>
                  <Trash size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button onClick={create} className="w-full bg-[#005CFF] hover:bg-[#0047CC]" data-testid="webhook-add">
        <Plus size={13} weight="bold" /> Nouveau webhook
      </Button>

      <WebhookEditor
        open={!!editing}
        initial={editing}
        onOpenChange={(o) => !o && setEditing(null)}
        available={available}
        onSaved={load}
      />
      <DeliveriesDialog
        open={!!deliveries.webhookId}
        onOpenChange={(o) => !o && setDeliveries({ webhookId: null, list: [] })}
        data={deliveries}
      />
    </div>
  );
}

function WebhookEditor({ open, initial, onOpenChange, available, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  // Precompute the per-group filtered event list to avoid re-filtering on every render
  const availableGroups = useMemo(
    () => EVENT_GROUPS.map((g) => ({ label: g.label, events: g.events.filter((e) => available.includes(e)) })).filter((g) => g.events.length > 0),
    [available]
  );

  useEffect(() => {
    if (initial) setForm({
      name: initial.name || "",
      url: initial.url || "",
      events: initial.events || [],
      enabled: initial.enabled !== false,
    });
  }, [initial]);

  const toggleEvent = (evt) => {
    setForm((f) => ({
      ...f,
      events: (f.events || []).includes(evt)
        ? f.events.filter((e) => e !== evt)
        : [...(f.events || []), evt],
    }));
  };

  const save = async () => {
    if (!form.name?.trim() || !form.url?.trim()) { toast.error("Nom + URL requis"); return; }
    if ((form.events || []).length === 0) { toast.error("Choisis au moins 1 événement"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), url: form.url.trim(), events: form.events, enabled: form.enabled };
      let res;
      if (isEdit) res = await api.patch(`/admin/webhooks/${initial.id}`, payload);
      else res = await api.post("/admin/webhooks", payload);
      toast.success(isEdit ? "Webhook mis à jour" : "Webhook créé");
      // If new webhook — show the secret once (it's persistent but we highlight it)
      if (!isEdit && res.data?.secret) {
        setTimeout(() => {
          toast.info(`Secret : ${res.data.secret.slice(0, 12)}… (visible dans le détail)`, { duration: 6000 });
        }, 400);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#111] border-white/10 text-white rounded-2xl" data-testid="webhook-editor">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Modifier le webhook" : "Nouveau webhook"}</DialogTitle>
          <DialogDescription className="text-xs text-white/60">
            L&apos;URL recevra un POST JSON à chaque événement coché. Compatible n8n · Zapier · Make.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/60">Nom</label>
            <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Slack équipe" className="bg-white/5 border-white/10 mt-1" data-testid="webhook-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/60">URL webhook</label>
            <Input value={form.url || ""} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://n8n.example.com/webhook/xxx" className="bg-white/5 border-white/10 font-mono text-xs mt-1" data-testid="webhook-url" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">Événements à envoyer ({(form.events || []).length})</div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {availableGroups.map((g) => (
                <div key={g.label}>
                  <div className="text-[10px] uppercase text-white/40 mb-1">{g.label}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {g.events.map((e) => {
                      const on = (form.events || []).includes(e);
                      return (
                        <button
                          key={e}
                          type="button"
                          onClick={() => toggleEvent(e)}
                          className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${on ? "bg-[#005CFF] border-[#005CFF] text-white" : "bg-white/5 border-white/10 text-white/60 hover:border-white/25"}`}
                          data-testid={`webhook-event-${e}`}
                        >
                          {e}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-white/[0.03] border border-white/10 p-3">
            <div className="text-sm">Actif</div>
            <Switch checked={form.enabled !== false} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
          </div>
          {isEdit && initial?.secret && <SecretRow secret={initial.secret} webhookId={initial.id} onRotated={onSaved} />}
        </div>

        <div className="flex gap-2 mt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="flex-1 border border-white/10">Annuler</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="webhook-save">
            {saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SecretRow({ secret, webhookId, onRotated }) {
  const [reveal, setReveal] = useState(false);
  const [rotating, setRotating] = useState(false);
  const rotate = async () => {
    if (!window.confirm("Régénérer le secret ? L'ancien deviendra invalide côté récepteur.")) return;
    setRotating(true);
    try {
      const r = await api.post(`/admin/webhooks/${webhookId}/rotate-secret`);
      toast.success("Secret régénéré — mets-le à jour dans ton n8n/Zapier");
      navigator.clipboard.writeText(r.data.secret).catch(() => {});
      onRotated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRotating(false); }
  };
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3 space-y-1.5">
      <div className="text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1.5">
        <Info size={12} /> Secret HMAC (X-BKflow-Signature)
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-xs font-mono bg-black/40 rounded px-2 py-1 break-all">
          {reveal ? secret : "•".repeat(Math.min(secret.length, 32))}
        </code>
        <button onClick={() => setReveal(!reveal)} className="text-[10px] text-white/60 hover:text-white/90 px-2">{reveal ? "Masquer" : "Voir"}</button>
        <button onClick={() => { navigator.clipboard.writeText(secret); toast.success("Copié"); }} className="text-white/40 hover:text-[#7CB2FF] p-1">
          <Copy size={12} />
        </button>
        <button onClick={rotate} disabled={rotating} className="text-white/40 hover:text-amber-400 p-1" title="Régénérer">
          <ArrowsClockwise size={12} className={rotating ? "animate-spin" : ""} />
        </button>
      </div>
    </div>
  );
}

function DeliveriesDialog({ open, onOpenChange, data }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#111] border-white/10 text-white rounded-2xl" data-testid="deliveries-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Historique — {data.name}</DialogTitle>
          <DialogDescription className="text-xs text-white/60">50 derniers appels au webhook.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 max-h-96 overflow-y-auto">
          {data.list.length === 0 && (
            <div className="text-center text-sm text-white/40 py-6">Aucun envoi pour l&apos;instant. Utilise le bouton « Tester » pour envoyer un ping.</div>
          )}
          {data.list.map((d) => (
            <div key={d.id} className="rounded-lg bg-white/[0.03] border border-white/10 p-2.5 flex items-start gap-2 text-xs">
              {d.ok
                ? <CheckCircle size={14} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" />
                : <XCircle size={14} weight="fill" className="text-red-400 shrink-0 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px]">{d.event}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${d.ok ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                    {d.status || d.error || "err"}
                  </span>
                  <span className="text-[9.5px] text-white/40">{d.duration_ms}ms</span>
                  <span className="text-[9.5px] text-white/40 ml-auto">{new Date(d.at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                {d.payload_preview && (
                  <code className="block text-[10px] text-white/50 mt-1 font-mono break-all line-clamp-2">{d.payload_preview}</code>
                )}
                {d.error && <div className="text-[10px] text-red-300 mt-0.5">{d.error}</div>}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
